import { useCallback, useMemo, useRef, useState } from 'react';
import { ISelectOption } from 'interfaces';

import { getOptionLabelByValue } from 'utils';

import { useClickOutside } from './useClickOutside';

interface UseSelectProps<T, P> {
  value: T | T[];
  placeholder?: string;
  unknownOptionPlaceholder?: string;
  options: ISelectOption<T, P>[];
  disabled?: boolean;
  onSelect: (value: T, option: ISelectOption<T, P>) => void;
  isMultiple?: boolean;
}

export const useSelect = <T, P, E extends HTMLElement>({
  value,
  options,
  unknownOptionPlaceholder,
  placeholder,
  disabled,
  onSelect,
  isMultiple,
}: UseSelectProps<T, P>) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<E>(null);

  const selectedOptionLabel = useMemo(
    () =>
      getOptionLabelByValue(
        value,
        options,
        unknownOptionPlaceholder,
        placeholder
      ),
    [value, options]
  );

  const handleIsOpenToggle = useCallback(
    () => !disabled && setIsOpen((value) => !value),
    [disabled]
  );

  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleSelect = useCallback(
    (value: T, option: ISelectOption<T, P>) => {
      onSelect(value, option);
      !isMultiple && setIsOpen(false);
    },
    [onSelect]
  );

  useClickOutside(containerRef, handleClose);

  return {
    containerRef,
    isOpen,
    selectedOptionLabel,
    setIsOpen,
    handleIsOpenToggle,
    handleClose,
    handleOpen,
    handleSelect,
  };
};
